import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import content from '../content/site-content.ko.json';
import './Main.css';

const publicUrl = process.env.PUBLIC_URL || '';

function Main() {
  const location = useLocation();
  const { hero, business, showcase, process, portfolio, contactCta } = content;
  const [activeShowcase, setActiveShowcase] = useState(null);

  const closeShowcase = useCallback(() => setActiveShowcase(null), []);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [location.hash]);

  useEffect(() => {
    if (!activeShowcase) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeShowcase();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [activeShowcase, closeShowcase]);

  return (
    <main className="studio-home">
      <section className="studio-hero" id="video">
        <div className="studio-hero__media" style={{ backgroundImage: `url(${publicUrl}/images/first.png)` }}>
          <video
            className="studio-hero__video"
            autoPlay
            muted
            loop
            playsInline
            poster={`${publicUrl}/images/first.png`}
          >
            <source src={`${publicUrl}${hero.video}`} type="video/mp4" />
          </video>
          <div className="studio-hero__overlay" />
        </div>
        <div className="studio-hero__content container">
          <p className="studio-hero__overline">{hero.overline}</p>
          <h1 className="studio-hero__title">
            {hero.headlineBefore}{' '}
            <span className="studio-hero__highlight">{hero.headlineHighlight}</span>
            {hero.headlineMiddle}
            <br />
            {hero.headlineAfterBefore}{' '}
            <span className="studio-hero__highlight">{hero.headlineHighlightSecond}</span>
            {hero.headlineAfter}
          </h1>
          <p className="studio-hero__sub">{hero.subcopy}</p>
          <div className="studio-hero__ctas">
            <a
              href={hero.ctaPrimary.href}
              className="btn btn-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              {hero.ctaPrimary.label}
            </a>
            <Link to={hero.ctaSecondary.href} className="btn btn-secondary">
              {hero.ctaSecondary.label}
            </Link>
          </div>
          <div className="studio-hero__chips">
            {hero.chips.map((chip) => (
              <span key={chip.label} className="studio-chip">
                <span aria-hidden="true">{chip.icon}</span> {chip.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="studio-section" id="solution">
        <div className="container">
          <div className="studio-section__head">
            <h2>{business.title}</h2>
            <p>{business.subtitle}</p>
          </div>
          <div className="studio-business-grid">
            {business.items.map((item) => (
              <article key={item.id} className={`studio-card studio-card--${item.accent}`} id={item.id === 'dev' ? 'dev' : item.id === 'solution' ? 'build' : undefined}>
                <span className="studio-card__weight">{item.weight}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <ul className="studio-card__tags">
                  {item.tags.map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
                {item.href.startsWith('http') ? (
                  <a href={item.href} className="studio-card__link" target="_blank" rel="noopener noreferrer">
                    자세히 보기 →
                  </a>
                ) : (
                  <Link to={item.href} className="studio-card__link">
                    자세히 보기 →
                  </Link>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="studio-section studio-section--muted" id="showcase">
        <div className="container">
          <div className="studio-section__head">
            <h2>{showcase.title}</h2>
            <p>{showcase.subtitle}</p>
          </div>
          <div className="studio-showcase-grid">
            {showcase.items.map((item) => (
              <button
                key={item.video}
                type="button"
                className="studio-showcase-item"
                onClick={() => setActiveShowcase(item)}
                aria-label={`${item.title} 영상 보기`}
              >
                <div className="studio-showcase-thumb">
                  <video
                    className="studio-showcase-video"
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                    onMouseLeave={(e) => {
                      e.currentTarget.pause();
                      e.currentTarget.currentTime = 0;
                    }}
                  >
                    <source src={`${publicUrl}/mov/${item.video}`} type="video/mp4" />
                  </video>
                  <span className="studio-showcase-play" aria-hidden="true">▶</span>
                </div>
                <span className="studio-showcase-cat">{item.category}</span>
                <span className="studio-showcase-title">{item.title}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {activeShowcase && (
        <div className="showcase-modal" onClick={closeShowcase} role="presentation">
          <div
            className="showcase-modal__dialog"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={activeShowcase.title}
          >
            <button type="button" className="showcase-modal__close" onClick={closeShowcase} aria-label="닫기">
              ×
            </button>
            <div className="showcase-modal__video-wrap">
              <video
                key={activeShowcase.video}
                className="showcase-modal__video"
                src={`${publicUrl}/mov/${activeShowcase.video}`}
                controls
                autoPlay
                playsInline
              />
            </div>
            <div className="showcase-modal__meta">
              <span className="showcase-modal__cat">{activeShowcase.category}</span>
              <h3>{activeShowcase.title}</h3>
            </div>
          </div>
        </div>
      )}

      <section className="studio-section" id="process">
        <div className="container">
          <div className="studio-section__head">
            <h2>{process.title}</h2>
            <p>{process.subtitle}</p>
          </div>
          <ol className="studio-process">
            {process.steps.map((step) => (
              <li key={step.step} className="studio-process__step">
                <span className="studio-process__num">{step.step}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="studio-section studio-section--muted" id="portfolio">
        <div className="container">
          <div className="studio-section__head">
            <h2>{portfolio.title}</h2>
            <p>{portfolio.subtitle}</p>
          </div>
          <div className="studio-portfolio-grid">
            {portfolio.items.map((item) => {
              const card = (
                <>
                  <div className="studio-portfolio-card__top">
                    <span className="studio-portfolio-card__tag">{item.tag}</span>
                    {item.status && <span className="studio-portfolio-card__status">{item.status}</span>}
                  </div>
                  <h3>{item.name}</h3>
                  <p>{item.description}</p>
                </>
              );

              if (!item.href) {
                return (
                  <article key={item.name} className="studio-portfolio-card studio-portfolio-card--disabled">
                    {card}
                  </article>
                );
              }

              if (item.external) {
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    className="studio-portfolio-card"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {card}
                  </a>
                );
              }

              return (
                <Link key={item.name} to={item.href} className="studio-portfolio-card">
                  {card}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="studio-section studio-contact-cta" id="contact">
        <div className="container studio-contact-cta__inner">
          <div>
            <h2>{contactCta.title}</h2>
            <p>{contactCta.subtitle}</p>
          </div>
          <Link to="/contact" className="btn btn-primary">
            {contactCta.button}
          </Link>
        </div>
      </section>
    </main>
  );
}

export default Main;
